/**
 * Data layer for the Habit Tracker section — backed by the Habits Hub admin
 * APIs (Backend: controllers/admin/v1/habitsHubController.js).
 *
 * Every page in container/HabitsHub reads through these fetchers. Each fetcher
 * unwraps the standard `{ data, meta: { code } }` response and adapts the
 * payload to the shape the pages consume. Read-only dashboards resolve `null`
 * on failure (pages render placeholders); list fetchers throw so their
 * callers' try/catch shows an empty table.
 */

import { Api } from '../../api';
import { unwrap } from '../SleepHub/sleepHubApi';

export { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';

/**
 * Shared shaping for the tracker dashboards — admin + company.
 *
 * Both panels are served by the same builder in
 * services/habitsHubService.js, so both read the payload through here and can
 * render the same panels.
 */
export const adaptDashboard = (data) => ({
  // Headline
  totalHabits: data.totalHabits ?? 0,
  usersTracking: data.usersTracking ?? 0,
  totalUserHabits: data.totalUserHabits ?? 0,
  activeUserHabits: data.activeUserHabits ?? 0,
  completions30d: data.completions30d ?? 0,
  lifetimeCompletions: data.lifetimeCompletions ?? 0,
  avgCurrentStreak: data.avgCurrentStreak ?? 0,
  bestStreak: data.bestStreak ?? 0,

  /**
   * Two different "is it working" figures:
   *  - completionRate: of everything logged, how much was actually done;
   *  - stickPercentage: of every habit adopted, how many got off the ground.
   */
  loggedActivities30d: data.loggedActivities30d ?? 0,
  completionRate: data.completionRate ?? 0,
  neverCompletedHabits: data.neverCompletedHabits ?? 0,
  stickPercentage: data.stickPercentage ?? 0,

  remindersOn: data.remindersOn ?? 0,
  remindersOff: data.remindersOff ?? 0,
  commonCompletionTime: data.commonCompletionTime || null,

  // Backend zero-fills the last 30 days; charts consume plain series.
  completionsPerDay: (data.completionsPerDay || []).map((day) => day.completions ?? 0),
  newHabitsPerDay: (data.newHabitsPerDay || []).map((day) => day.started ?? 0),
  weekdayPattern: data.weekdayPattern || [],

  topHabits: (data.topHabits || []).map((habit) => ({
    id: habit._id,
    name: habit.name,
    source: habit.source,
    users: habit.users,
    activeUsers: habit.activeUsers ?? null,
    completions: habit.completions
  })),

  statusDistribution: data.statusDistribution || [],
  sourceSplit: data.sourceSplit || [],
  frequencySplit: data.frequencySplit || [],
  outcomeSplit: data.outcomeSplit || [],
  streakDistribution: data.streakDistribution || [],
  adherenceDistribution: data.adherenceDistribution || [],
  habitsPerUserDistribution: data.habitsPerUserDistribution || [],
  trackingConsistency: data.trackingConsistency || [],
  scheduleTimeDistribution: data.scheduleTimeDistribution || [],
  timeOfDayDistribution: data.timeOfDayDistribution || []
});

/**
 * Dashboard aggregates. `scope` is '' (combined), 'B2C' or 'B2B'.
 */
export const fetchHabitsDashboard = async (scope = '') => {
  try {
    return adaptDashboard(unwrap(await Api.getHabitsHubDashboard(scope)));
  } catch (error) {
    console.error('fetchHabitsDashboard error:', error);
    return null;
  }
};

export const fetchHabitsCompanies = async ({ page = 1, perPage = 10, search = '' } = {}) => {
  const data = unwrap(await Api.getHabitsHubCompanies(page, perPage, search));
  return {
    data: (data.data || []).map((company) => ({
      ...company,
      // The table's partner-profile cell reads the logo from company_logo.
      company_logo: company.companyLogo || null
    })),
    totalCount: data.totalCount || 0
  };
};

export const fetchHabitsCompanyDetail = async (companyId) => {
  try {
    const data = unwrap(await Api.getHabitsHubCompanyDetail(companyId));
    return { company: data.company || null };
  } catch (error) {
    console.error('fetchHabitsCompanyDetail error:', error);
    return { company: null };
  }
};

export const fetchHabitsUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  accountType = '', // '' | 'B2C' | 'B2B'
  companyId = ''
} = {}) => {
  const data = unwrap(
    await Api.getHabitsHubUsers(page, perPage, search, accountType, companyId)
  );
  return { data: data.data || [], totalCount: data.totalCount || 0 };
};

/**
 * Shared shaping for the user-detail page — admin + company.
 *
 * The one transformation that matters is the trend series: the backend sends
 * `[{ date, completions }]` and the chart plots plain numbers. Feeding the
 * objects straight through renders NaN paths and takes the whole page down,
 * so both panels adapt through here rather than each remembering to flatten.
 */
export const adaptUserDetail = (data) => ({
  user: data.user,
  habits: data.habits || [],
  completionsPerDay: (data.completionsPerDay || []).map((day) => day.completions ?? 0)
});

export const fetchHabitsUserDetail = async (userId) => {
  try {
    return adaptUserDetail(unwrap(await Api.getHabitsHubUserDetail(userId)));
  } catch (error) {
    console.error('fetchHabitsUserDetail error:', error);
    return null;
  }
};
